import React, { useState } from 'react';
import { getUiLocale } from '@/lib/dates';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { Send, Check, X, FileCheck2, Archive, AlertCircle, Loader2 } from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';

/**
 * Library MVP · EvaluationLifecyclePanel
 *
 * Single role-aware status pill + transition CTA row. Used inside
 * AdminQuestionnaireDetail.jsx. Reused by any future surface that needs to
 * visualise / drive a Library lifecycle transition.
 *
 * Visibility rules (mirror routes/admin/questionnaires.py):
 *   - hm_advisor  → can submit-for-review on own draft/needs_revision.
 *   - hm_admin    → can submit, approve, request-changes, publish, archive.
 *
 * Status → tone map (reused tokens, no new design):
 *   draft                  → muted
 *   submitted_for_review   → info
 *   needs_revision         → warning
 *   approved               → primary
 *   published              → success
 *   archived               → muted (line-through)
 */

const STATUS_TONE = {
  draft: 'bg-muted text-muted-foreground border-border',
  submitted_for_review: 'bg-info/10 text-info border-info/20',
  needs_revision: 'bg-warning/10 text-warning border-warning/20',
  approved: 'bg-primary/10 text-primary border-primary/20',
  published: 'bg-success/10 text-success border-success/20',
  archived: 'bg-muted text-muted-foreground/70 border-border line-through',
};

export function EvaluationLifecyclePanel({ evaluation, onTransitioned }) {
  const { t } = useTranslation();
  const { user } = useApp();
  const role = user?.role;
  const status = evaluation?.status || 'draft';
  const [busy, setBusy] = useState(null);
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [reason, setReason] = useState('');

  if (!evaluation) return null;

  const wrap = async (key, fn) => {
    setBusy(key);
    try {
      const next = await fn();
      toast.success(t(`library.lifecycle.toast.${key}Success`));
      onTransitioned?.(next);
    } catch (e) {
      toast.error(t('toasts.failed'), { description: formatApiError(e) });
    } finally {
      setBusy(null);
    }
  };

  const submit = () => wrap('submit', () => adminApi.submitEvaluationForReview(evaluation.id));
  const approve = () => wrap('approve', () => adminApi.approveEvaluation(evaluation.id));
  const publish = () => wrap('publish', () => adminApi.publishEvaluation(evaluation.id));
  const archive = () => wrap('archive', () => adminApi.archiveQuestionnaire(evaluation.id));
  const revertToApproved = () => wrap('revertToApproved', () => adminApi.revertEvaluationToApproved(evaluation.id));
  const revertToDraft = () => wrap('revertToDraft', () => adminApi.revertEvaluationToDraft(evaluation.id));

  const requestChangesSubmit = async () => {
    if ((reason || '').trim().length < 10) {
      toast.error(t('library.lifecycle.reasonTooShort'));
      return;
    }
    setBusy('requestChanges');
    try {
      const next = await adminApi.requestEvaluationChanges(evaluation.id, reason.trim());
      toast.success(t('library.lifecycle.toast.requestChangesSuccess'));
      setReasonDialogOpen(false);
      setReason('');
      onTransitioned?.(next);
    } catch (e) {
      toast.error(t('toasts.failed'), { description: formatApiError(e) });
    } finally {
      setBusy(null);
    }
  };

  const isAdmin = role === 'hm_admin' || role === 'hm_reviewer';
  const isAdvisor = role === 'hm_advisor';
  const isOwner =
    evaluation.created_by === user?.id || evaluation.submitted_by === user?.id;

  // Compute the set of allowed CTAs given role + status.
  const can = {
    submit: (isAdmin || (isAdvisor && isOwner)) && (status === 'draft' || status === 'needs_revision'),
    requestChanges: isAdmin && status === 'submitted_for_review',
    approve: isAdmin && status === 'submitted_for_review',
    publish: isAdmin && status === 'approved',
    archive: isAdmin && status !== 'archived',
    revertToApproved: isAdmin && status === 'published',
    revertToDraft: isAdmin && status === 'published',
  };

  const comments = Array.isArray(evaluation.review_comments) ? evaluation.review_comments : [];
  const latestComment = comments[comments.length - 1]?.reason || evaluation.needs_revision_reason || null;

  const tone = STATUS_TONE[status] || STATUS_TONE.draft;

  return (
    <Card className="p-4 shadow-card" data-testid="evaluation-lifecycle-panel">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 mr-auto">
          <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
            {t('library.lifecycle.status')}
          </span>
          <Badge
            variant="outline"
            className={`${tone} font-medium`}
            data-testid={`evaluation-status-${status}`}
          >
            {t(`library.lifecycle.statuses.${status}`, { defaultValue: status })}
          </Badge>
          {status === 'archived' && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    data-testid="evaluation-archived-tooltip-trigger"
                    className="text-xs uppercase tracking-wider font-semibold text-muted-foreground cursor-help underline decoration-dotted decoration-from-font underline-offset-2"
                  >
                    {t('library.lifecycle.archivedTooltip.label', { defaultValue: 'Why archived?' })}
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="max-w-sm text-xs leading-relaxed"
                  data-testid="evaluation-archived-tooltip-content"
                >
                  {t('library.lifecycle.archivedTooltip.body', {
                    defaultValue: 'Archived evaluations are read-only. They cannot be assigned, but their history and results remain accessible for audit.',
                  })}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {status === 'needs_revision' && latestComment && (
            <div className="flex items-start gap-1.5 text-xs text-warning ml-2"
                 data-testid="evaluation-revision-reason">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span className="italic max-w-md">{latestComment}</span>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {can.submit && (
            <Button
              size="sm"
              variant="default"
              className="gap-1.5"
              disabled={busy === 'submit'}
              onClick={submit}
              data-testid="evaluation-cta-submit"
            >
              {busy === 'submit' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              {t('library.lifecycle.cta.submit')}
            </Button>
          )}
          {can.requestChanges && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              onClick={() => setReasonDialogOpen(true)}
              data-testid="evaluation-cta-request-changes"
            >
              <X className="h-3.5 w-3.5" />
              {t('library.lifecycle.cta.requestChanges')}
            </Button>
          )}
          {can.approve && (
            <Button
              size="sm"
              variant="default"
              className="gap-1.5"
              disabled={busy === 'approve'}
              onClick={approve}
              data-testid="evaluation-cta-approve"
            >
              {busy === 'approve' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              {t('library.lifecycle.cta.approve')}
            </Button>
          )}
          {can.publish && (
            <Button
              size="sm"
              className="gap-1.5 bg-gradient-primary"
              disabled={busy === 'publish'}
              onClick={publish}
              data-testid="evaluation-cta-publish"
            >
              {busy === 'publish' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileCheck2 className="h-3.5 w-3.5" />}
              {t('library.lifecycle.cta.publish')}
            </Button>
          )}
          {can.archive && (
            <Button
              size="sm"
              variant="ghost"
              className="gap-1.5 text-muted-foreground"
              disabled={busy === 'archive'}
              onClick={archive}
              data-testid="evaluation-cta-archive"
            >
              {busy === 'archive' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Archive className="h-3.5 w-3.5" />}
              {t('library.lifecycle.cta.archive')}
            </Button>
          )}
          {can.revertToApproved && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              disabled={busy === 'revertToApproved'}
              onClick={revertToApproved}
              data-testid="evaluation-cta-revert-to-approved"
            >
              {busy === 'revertToApproved' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileCheck2 className="h-3.5 w-3.5" />}
              {t('library.lifecycle.cta.revertToApproved', { defaultValue: 'Unpublish (back to Approved)' })}
            </Button>
          )}
          {can.revertToDraft && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              disabled={busy === 'revertToDraft'}
              onClick={revertToDraft}
              data-testid="evaluation-cta-revert-to-draft"
            >
              {busy === 'revertToDraft' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
              {t('library.lifecycle.cta.revertToDraft', { defaultValue: 'Reopen as Draft' })}
            </Button>
          )}
        </div>
      </div>

      {comments.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border/70" data-testid="evaluation-review-history">
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
              {t('library.lifecycle.reviewHistory.title', { defaultValue: 'Review history' })}
            </p>
            <span className="text-xs text-muted-foreground tabular-nums">
              {t('library.lifecycle.reviewHistory.count', { count: comments.length, defaultValue: '{{count}} entries' })}
            </span>
          </div>
          <ol className="space-y-2.5">
            {comments.map((c, idx) => (
              <li
                key={c.id || idx}
                className="rounded-lg border border-warning/20 bg-warning/5 px-3 py-2 text-xs leading-relaxed"
                data-testid={`evaluation-review-history-entry-${idx + 1}`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-medium text-foreground/80">
                    #{idx + 1} · {c.actor_name || c.actor_role || c.actor_user_id || '—'}
                  </span>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {c.at ? new Date(c.at).toLocaleString(getUiLocale()) : ''}
                  </span>
                </div>
                <p className="text-foreground/80 whitespace-pre-wrap">{c.reason}</p>
              </li>
            ))}
          </ol>
        </div>
      )}

      <Dialog open={reasonDialogOpen} onOpenChange={setReasonDialogOpen}>
        <DialogContent data-testid="evaluation-request-changes-dialog">
          <DialogHeader>
            <DialogTitle>{t('library.lifecycle.cta.requestChanges')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">{t('library.lifecycle.reasonLabel')}</Label>
            <Textarea
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('library.lifecycle.reasonPlaceholder')}
              data-testid="evaluation-request-changes-reason"
            />
            <p className="text-xs text-muted-foreground">
              {t('library.lifecycle.reasonHint')}
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReasonDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={requestChangesSubmit}
              disabled={busy === 'requestChanges' || (reason || '').trim().length < 10}
              data-testid="evaluation-request-changes-confirm"
            >
              {busy === 'requestChanges' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : null}
              {t('library.lifecycle.cta.requestChanges')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default EvaluationLifecyclePanel;
